  <template>
  <div class="rider-deliveries pa-4">
    <div class="d-flex justify-space-between align-center mb-6">
      <div>
        <h1 class="page-title">งานจัดส่ง</h1>
        <p class="page-subtitle">รายการงานจัดส่งที่รอดำเนินการ</p>
      </div>
    </div>

    <!-- Filter Tabs -->
    <v-tabs v-model="activeTab" class="mb-4" background-color="transparent">
      <v-tab>งานว่างใกล้คุณ</v-tab>
      <v-tab>รอรับสินค้า</v-tab>
      <v-tab>กำลังดำเนินการ</v-tab>
      <v-tab>ส่งสำเร็จ</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <v-tab-item v-for="tab in 4" :key="tab">
        <v-card class="pa-4" elevation="0" style="border-radius: 16px;">
          <v-data-table
            :headers="headers"
            :items="deliveries"
            :items-per-page="10"
            class="elevation-0"
            :loading="loading"
          >
            <template v-slot:[`item.orderNumber`]="{ item }">
              <span class="font-weight-bold">#{{ item.orderNumber }}</span>
            </template>

            <template v-slot:[`item.pickup`]="{ item }">
              <div>
                <div class="font-weight-medium text-truncate" style="max-width: 150px;">{{ item.sellerName }}</div>
                <div class="text-caption grey--text text-truncate" style="max-width: 150px;">{{ item.pickupLocation }}</div>
              </div>
            </template>

            <template v-slot:[`item.delivery`]="{ item }">
              <div>
                <div class="font-weight-medium text-truncate" style="max-width: 150px;">{{ item.customerName }}</div>
                <div class="text-caption grey--text text-truncate" style="max-width: 150px;">{{ item.deliveryLocation }}</div>
              </div>
            </template>

            <template v-slot:[`item.deliveryFee`]="{ item }">
              <span class="font-weight-bold success--text">฿{{ formatNumber(item.deliveryFee) }}</span>
            </template>

            <template v-slot:[`item.status`]="{ item }">
              <v-chip small :color="getStatusColor(item.status)" dark>
                {{ getStatusText(item.status) }}
              </v-chip>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <div class="d-flex gap-2">
                <v-btn
                  v-if="item.status === 'seeking_rider'"
                  small
                  color="warning"
                  @click="acceptJob(item)"
                  :disabled="hasActiveOrder"
                >
                  กดรับงาน
                </v-btn>
                <v-btn
                  v-if="['processing', 'paid'].includes(item.status)"
                  small
                  color="primary"
                  @click="pickupOrder(item)"
                >
                  รับสินค้า
                </v-btn>
                <v-btn
                  v-if="['picked_up', 'delivering', 'shipped'].includes(item.status)"
                  small
                  color="success"
                  @click="completeDelivery(item)"
                >
                  จัดส่งแล้ว
                </v-btn>
                <v-btn icon small @click="viewDetails(item)">
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
export default {
  name: "RiderDeliveries",

  data() {
    return {
      activeTab: 0,
      loading: false,
      headers: [
        { text: "หมายเลขคำสั่งซื้อ", value: "orderNumber", sortable: false },
        { text: "รับสินค้าจาก", value: "pickup", sortable: false },
        { text: "ส่งถึง", value: "delivery", sortable: false },
        { text: "ค่าจัดส่ง", value: "deliveryFee" },
        { text: "สถานะ", value: "status", sortable: false },
        { text: "จัดการ", value: "actions", sortable: false, width: "200px" },
      ],
      deliveries: [],
      hasActiveOrder: false,
    };
  },

  computed: {
    filteredDeliveries() {
      // The API already filters based on the tab query, but we can also filter locally for smoothness
      if (this.activeTab === 0) {
        return this.deliveries;
      } else if (this.activeTab === 1) {
        return this.deliveries.filter(d => ["processing", "paid"].includes(d.status));
      } else {
        return this.deliveries.filter(d => ["picked_up", "delivering", "shipped"].includes(d.status));
      }
    },
  },

  watch: {
    activeTab() {
      this.fetchDeliveries();
    }
  },

  mounted() {
    this.fetchDeliveries();
  },

  methods: {
    async fetchDeliveries() {
      this.loading = true;
      try {
        let tabParam = 'all';
        if (this.activeTab === 0) tabParam = 'available';
        if (this.activeTab === 1) tabParam = 'pending';
        if (this.activeTab === 2) tabParam = 'ongoing';
        if (this.activeTab === 3) tabParam = 'completed';

        const response = await this.$axios.get(`${this.$domain_api}/api/v1/auth/rider/orders?tab=${tabParam}`, {
          headers: { Authorization: `Bearer ${this.$store.getters.accessToken}` }
        });

        if (response.data.success) {
          this.deliveries = response.data.data.map(order => ({
            id: order.id,
            orderNumber: order.orderNumber,
            status: order.status,
            sellerName: order.seller?.shopName || "ไม่ระบุร้านค้า",
            pickupLocation: `${order.seller?.address?.detail || ''} ${order.seller?.address?.subDistrict?.name || ''}`.trim() || order.seller?.address?.detail || "ไม่ระบุตำแหน่ง",
            customerName: `${order.buyer?.firstName || ''} ${order.buyer?.lastName || ''}`.trim() || order.address?.firstName || "ลูกค้า",
            deliveryLocation: order.address?.detail || "ไม่ระบุตำแหน่ง",
            deliveryFee: order.shipFee || 0,
          }));
          this.hasActiveOrder = response.data.hasActiveOrder;
        }
      } catch (error) {
        console.error("Fetch deliveries error:", error);
        this.$swal.fire('ข้อผิดพลาด', 'ไม่สามารถดึงข้อมูลรายการส่งสินค้าได้', 'error');
      } finally {
        this.loading = false;
      }
    },

    formatNumber(num) {
      return new Intl.NumberFormat("th-TH").format(num);
    },

    getStatusColor(status) {
      const colors = {
        seeking_rider: "amber",
        pending: "orange",
        processing: "blue",
        accepted: "blue",
        picked_up: "purple",
        delivering: "indigo",
        shipped: "indigo",
        delivered: "success",
        cancelled: "error",
      };
      return colors[status] || "grey";
    },

    getStatusText(status) {
      const texts = {
        seeking_rider: "งานว่าง - รอไรเดอร์",
        pending: "รอรับงาน",
        processing: "รอรับสินค้า",
        accepted: "รับงานแล้ว",
        picked_up: "รับสินค้าแล้ว",
        delivering: "กำลังจัดส่ง",
        shipped: "กำลังจัดส่ง",
        delivered: "จัดส่งแล้ว",
        cancelled: "ยกเลิก",
      };
      return texts[status] || status;
    },

    async acceptJob(item) {
      if (this.hasActiveOrder) {
        this.$swal.fire('คุณมีงานที่ยังไม่เสร็จ', 'คุณต้องจัดส่งออเดอร์ปัจจุบันให้สำเร็จก่อนจึงจะสามารถรับงานใหม่ได้', 'warning');
        return;
      }
      try {
        const response = await this.$axios.post(`${this.$domain_api}/api/v1/orders/${item.id}/rider-accept`, {}, {
          headers: { Authorization: `Bearer ${this.$store.getters.accessToken}` }
        });
        if (response.data.success) {
          this.$swal.fire({
            icon: 'success',
            title: 'รับงานสำเร็จ',
            text: 'คุณได้รับงานจัดส่งนี้แล้ว กรุณาไปรับสินค้าที่ร้านค้า',
            timer: 2000,
            showConfirmButton: false
          });
          this.fetchDeliveries();
        }
      } catch (error) {
        this.$swal.fire('ข้อผิดพลาด', error.response?.data?.message || 'ไม่สามารถรับงานได้', 'error');
      }
    },

    async pickupOrder(item) {
      try {
        const response = await this.$axios.post(`${this.$domain_api}/api/v1/orders/${item.id}/rider-pickup`, {}, {
          headers: { Authorization: `Bearer ${this.$store.getters.accessToken}` }
        });
        if (response.data.success) {
          this.$swal.fire({
            icon: 'success',
            title: 'รับสินค้าสำเร็จ',
            text: 'กำลังดำเนินการจัดส่งให้ลูกค้า',
            timer: 1500,
            showConfirmButton: false
          });
          this.fetchDeliveries();
        }
      } catch (error) {
        this.$swal.fire('ข้อผิดพลาด', error.response?.data?.message || 'ไม่สามารถทำรายการได้', 'error');
      }
    },

    async completeDelivery(item) {
      try {
        const response = await this.$axios.post(`${this.$domain_api}/api/v1/orders/${item.id}/rider-deliver`, {}, {
          headers: { Authorization: `Bearer ${this.$store.getters.accessToken}` }
        });
        if (response.data.success) {
          this.$swal.fire({
            icon: 'success',
            title: 'จัดส่งสำเร็จ',
            text: response.data.message,
            timer: 1500,
            showConfirmButton: false
          });
          this.fetchDeliveries();
        }
      } catch (error) {
        this.$swal.fire('ข้อผิดพลาด', error.response?.data?.message || 'ไม่สามารถทำรายการได้', 'error');
      }
    },

    viewDetails(item) {
      this.$router.push({ name: 'rider_order_detail', params: { id: item.id } });
    },
  },
};
</script>

<style scoped>
.rider-deliveries {
  background-color: #F8F9FB;
  min-height: 100vh;
  font-family: 'Sarabun', 'Noto Sans Thai', sans-serif !important;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
}

.page-subtitle {
  font-size: 14px;
  color: #9CA3AF;
}

.gap-2 {
  gap: 8px;
}
</style>
